import { promisedWait } from 'src/lib/common';
import { captureException } from 'src/utils/lib/sentry';
import { queryFacebookAdAccounts } from 'src/graphql/facebook';
import { queryLinkedinAdAccounts } from 'src/graphql/linkedin';
import { queryGoogleAdAccounts } from 'src/graphql/google';
import { CHANNEL } from 'src/data/channel';
import { connectFacebook } from 'src/services/facebook';
import connectToLinkedin from 'src/services/connectToLinkedin';
import connectToGoogle from 'src/services/connectToGoogle';
import {
  queryPerformanceCenterTextVariations,
  queryPerformanceCenterTextVariationsRequestStatus,
} from 'src/graphql/performance';
import { AbortError, AsyncRequestError } from 'src/lib/errors';

const rejectUnknownChannel = (error = 'Unknown social channel') => {
  return Promise.reject(new Error(error));
};

export const queryChannelAccounts = (channel) => {
  switch (channel) {
    case CHANNEL.facebook:
      return queryFacebookAdAccounts();
    case CHANNEL.linkedin:
      return queryLinkedinAdAccounts();
    case CHANNEL.google:
      return queryGoogleAdAccounts();

    default:
      return rejectUnknownChannel();
  }
};

export const connectToChannel = (channel) => {
  switch (channel) {
    case CHANNEL.facebook:
      return connectFacebook();
    case CHANNEL.linkedin:
      return connectToLinkedin();
    case CHANNEL.google:
      return connectToGoogle();

    default:
      return rejectUnknownChannel();
  }
};

const CREATION_POLLING_INTERVAL = 1000;

class CopiesExtractionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'CopiesExtractionError';
    this.code = code;
    this.message = message;
  }
}

export const isCopiesExtractionError = (error) => error instanceof CopiesExtractionError;

export const REQUEST_STATUS = {
  created: 'created',
  running: 'running',
  done: 'done',
  doneWithErrors: 'done_with_errors',
  partialDone: 'partial_done',
};

const unwrapErrors = (json) => {
  try {
    return JSON.parse(json);
  } catch (error) {
    captureException(error, {
      extra: {
        source: 'ads/waitForCopiesExtraction.js -> unwrapErrors',
        value: json,
      },
    });
    return [];
  }
};

const stringifyErrors = (errors) => errors.map((e) => e.detail).join('\n');

const checkKnownCopiesExtractionError = (errors, errorCode, newMessage = null) => {
  const error = errors?.find((e) => e?.detail === errorCode);
  if (error) {
    const message = newMessage || error?.detail;
    return new CopiesExtractionError(error.code, message);
  }
  return null;
};

const ADS_EXTRACTION_ERROR = {
  authError: 'AUTH_ERROR',
};

const KNOWN_ERRORS = [
  {
    code: ADS_EXTRACTION_ERROR.authError,
    message: 'Failed to fetch data, authentication issue please re-authorize.',
  },
];

const searchForKnownCopiesExtractionError = (errors) => {
  for (let i = 0; i < KNOWN_ERRORS.length; i += 1) {
    const knownErrorData = KNOWN_ERRORS[i];
    const error = checkKnownCopiesExtractionError(
      errors,
      knownErrorData.code,
      knownErrorData.message
    );
    if (error) {
      return error;
    }
  }

  return null;
};

export const waitForCopiesExtraction = async ({
  channel,
  workspaceId,
  notifications,
  options = null,
  setAvgCompletionPercent,
  textVariationsResultsCount,
}) => {
  if (options?.abortSignal?.aborted) {
    throw new AbortError();
  }
  const result = await queryPerformanceCenterTextVariationsRequestStatus(channel, workspaceId);

  if (!result) {
    return;
  }

  // If it was partially done - query text variations
  if (result.status === REQUEST_STATUS.partialDone) {
    const errors = unwrapErrors(result.errors);
    const errorsText = stringifyErrors(errors);
    notifications.displayWarning(errorsText);
    return await queryPerformanceCenterTextVariations(
      channel,
      workspaceId,
      textVariationsResultsCount
    );
  }

  // If it was successful - query text variations
  if (result.status === REQUEST_STATUS.done) {
    return await queryPerformanceCenterTextVariations(
      channel,
      workspaceId,
      textVariationsResultsCount
    );
  }

  // If creation failed - throw returned error upwards
  if (result.status === REQUEST_STATUS.doneWithErrors) {
    const errors = unwrapErrors(result.errors);

    const knownError = searchForKnownCopiesExtractionError(errors);
    if (knownError) {
      throw knownError;
    }

    const errorsText = stringifyErrors(errors);
    throw new AsyncRequestError(errorsText);
  }

  if (result.avgCompletionPercent || result.avgCompletionPercent === 0) {
    setAvgCompletionPercent(result.avgCompletionPercent, channel.replace('reviews_', ''));
  }

  // Wait sometime and check again
  await promisedWait(CREATION_POLLING_INTERVAL);

  return waitForCopiesExtraction({
    channel,
    workspaceId,
    notifications,
    options,
    setAvgCompletionPercent,
  });
};
